import { useEffect, useState } from 'react';

const FONT_CACHE = new Map();

// Local font pack shipped with the site. These files are exact extracted
// font resources from the supplied FNAC template. A local source keeps the
// browser on the PDF font instead of silently selecting a system fallback.
const LOCAL_FONT_BY_ALIAS = {
  PDF_FONT_75_5bb7121378: '/fonts/BAAAAA_MatriceBits-Z2.ttf',
  PDF_FONT_77_e436ad2c45: '/fonts/TimesNewRoman-Fallback-Regular.ttf',
  PDF_FONT_79_661693d4ec: '/fonts/LiberationSerif-Regular.ttf',
  PDF_FONT_81_a9f5f6bd1a: '/fonts/SegoeUI-Fallback-Regular.ttf',
  PDF_FONT_83_368dcf3795: '/fonts/Calibri-Fallback-Regular.ttf',
};

const LOCAL_FONT_BY_NAME = {
  matricebitsz2: '/fonts/BAAAAA_MatriceBits-Z2.ttf',
  timesnewromanpsmt: '/fonts/TimesNewRoman-Fallback-Regular.ttf',
  liberationserif: '/fonts/LiberationSerif-Regular.ttf',
  segoeui: '/fonts/SegoeUI-Fallback-Regular.ttf',
  calibri: '/fonts/Calibri-Fallback-Regular.ttf',
};

export const LOCAL_FONT_CATALOG = [
  {
    id: 'matricebits-z2',
    label: 'MatriceBits-Z2',
    cssFamily: 'RA_FONT_MATRICEBITS_Z2',
    previewFamily: 'RA_FONT_MATRICEBITS_Z2, "Courier New", monospace',
    systemFamily: '',
    url: '/fonts/BAAAAA_MatriceBits-Z2.ttf',
    weight: 400,
    style: 'normal',
    templateNames: ['MatriceBits-Z2'],
  },
  {
    id: 'times-new-roman',
    label: 'Times New Roman',
    cssFamily: 'RA_FONT_TIMES_NEW_ROMAN',
    previewFamily: '"Times New Roman", RA_FONT_TIMES_NEW_ROMAN, serif',
    systemFamily: 'Times New Roman',
    url: '/fonts/TimesNewRoman-Fallback-Regular.ttf',
    weight: 400,
    style: 'normal',
    templateNames: ['TimesNewRomanPSMT', 'Times New Roman'],
  },
  {
    id: 'liberation-serif',
    label: 'Liberation Serif',
    cssFamily: 'RA_FONT_LIBERATION_SERIF',
    previewFamily: '"Liberation Serif", RA_FONT_LIBERATION_SERIF, serif',
    systemFamily: 'Liberation Serif',
    url: '/fonts/LiberationSerif-Regular.ttf',
    weight: 400,
    style: 'normal',
    templateNames: ['LiberationSerif', 'Liberation Serif'],
  },
  {
    id: 'segoe-ui',
    label: 'Segoe UI',
    cssFamily: 'RA_FONT_SEGOE_UI',
    previewFamily: '"Segoe UI", RA_FONT_SEGOE_UI, Arial, sans-serif',
    systemFamily: 'Segoe UI',
    url: '/fonts/SegoeUI-Fallback-Regular.ttf',
    weight: 400,
    style: 'normal',
    templateNames: ['SegoeUI', 'Segoe UI', 'SegoeUI-Semilight'],
  },
  {
    id: 'calibri',
    label: 'Calibri',
    cssFamily: 'RA_FONT_CALIBRI',
    previewFamily: 'Calibri, RA_FONT_CALIBRI, Arial, sans-serif',
    systemFamily: 'Calibri',
    url: '/fonts/Calibri-Fallback-Regular.ttf',
    weight: 400,
    style: 'normal',
    templateNames: ['Calibri'],
  },
];

const LOCAL_FONT_BY_ID = new Map(LOCAL_FONT_CATALOG.map((font) => [font.id, font]));
const LOCAL_FONT_BY_CSS_FAMILY = new Map(LOCAL_FONT_CATALOG.map((font) => [font.cssFamily, font]));
const LOCAL_FONT_BY_URL = new Map(LOCAL_FONT_CATALOG.map((font) => [font.url, font]));

function localFontInfoFor(font = {}) {
  if (font?.id && LOCAL_FONT_BY_ID.has(font.id)) return LOCAL_FONT_BY_ID.get(font.id);
  const alias = String(font?.css_family || '').trim();
  if (alias && LOCAL_FONT_BY_CSS_FAMILY.has(alias)) return LOCAL_FONT_BY_CSS_FAMILY.get(alias);
  const url = localFontUrlFor(font);
  if (url && LOCAL_FONT_BY_URL.has(url)) return LOCAL_FONT_BY_URL.get(url);
  const names = [font?.basefont, font?.name, font?.embedded?.basename].map(normalize).filter(Boolean);
  for (const item of LOCAL_FONT_CATALOG) {
    if (names.includes(normalize(item.label)) || names.includes(normalize(item.id))) return item;
  }
  return null;
}

export async function ensureLocalFont(font) {
  if (isMatriceBitsFont(font)) return false;
  if (typeof document === 'undefined' || typeof FontFace !== 'function' || !font?.url || !font?.cssFamily) return false;
  try {
    const weight = String(Number(font.weight) || 400);
    const style = String(font.style || 'normal');
    const source = localFontSource(font);
    if (!source) return false;
    const face = new FontFace(font.cssFamily, source, { weight, style, display: 'block' });
    await face.load();
    document.fonts.add(face);
    return true;
  } catch {
    return false;
  }
}

function localFontSource(font) {
  const local = String(font?.systemFamily || '').trim();
  const url = String(font?.url || '').trim();
  const parts = [];
  if (local) parts.push(`local(\"${esc(local)}\")`);
  if (url) parts.push(`url(\"${url}\")`);
  return parts.join(', ');
}

function localFontUrlFor(font = {}) {
  const alias = String(font?.css_family || '').trim();
  if (alias && LOCAL_FONT_BY_ALIAS[alias]) return LOCAL_FONT_BY_ALIAS[alias];
  const names = [font?.basefont, font?.name, font?.embedded?.basename]
    .map(normalize)
    .filter(Boolean);
  for (const name of names) {
    if (LOCAL_FONT_BY_NAME[name]) return LOCAL_FONT_BY_NAME[name];
  }
  return '';
}

function esc(value) {
  return String(value || '').replaceAll('\\', '\\\\').replaceAll('"', '\\"');
}

function mimeFor(ext) {
  const value = String(ext || '').toLowerCase().replace(/^\./, '');
  return ({ ttf:'font/ttf', otf:'font/otf', woff:'font/woff', woff2:'font/woff2', cff:'font/otf' })[value] || 'application/octet-stream';
}

function formatFor(ext) {
  const value = String(ext || '').toLowerCase().replace(/^\./, '');
  return ({ ttf:'truetype', otf:'opentype', woff:'woff', woff2:'woff2' })[value] || '';
}

function normalize(value) {
  return String(value || '').replace(/^[A-Z]{6}\+/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

const MATRICE_BITS_ID = 'matricebits-z2';
const MATRICE_BITS_URL = '/fonts/BAAAAA_MatriceBits-Z2.ttf';
// The FNAC MatriceBits subset contains these 64 glyphs in the same custom
// order as glyph codes 1..64. We use that order only to rebuild a Unicode cmap
// for the site-wide local copy; the actual glyph outlines remain MatriceBits.
const MATRICE_BITS_UNICODE_SEQUENCE = Array.from(
  'FNAC GREOBLDPIVUSTMuméro:0825-.1etc/invdzs,fa9€l()6*34gkhb;jpHxq'
);
const MATRICE_BITS_FONT_ALIAS = 'RA_FONT_MATRICEBITS_Z2';
const MATRICE_BITS_UNIVERSAL_CACHE = new Map();

function isMatriceBitsFont(font = {}) {
  const candidates = [font?.id, font?.label, font?.name, font?.basefont, font?.css_family, font?.embedded?.basename]
    .map(normalize)
    .filter(Boolean);
  return candidates.some((value) => value.includes(MATRICE_BITS_ID));
}

function buildMatriceBitsUniversalMapping(raw) {
  const custom = getCustomGlyphSequence(raw);
  if (!custom || custom.unicode || !Array.isArray(custom.glyphs)) return null;
  const mapping = {};
  const limit = Math.min(MATRICE_BITS_UNICODE_SEQUENCE.length, custom.glyphs.length);
  for (let index = 0; index < limit; index += 1) {
    const cp = MATRICE_BITS_UNICODE_SEQUENCE[index]?.codePointAt(0);
    const gid = Number(custom.glyphs[index]);
    if (Number.isFinite(cp) && cp <= 0xffff && gid > 0) mapping[cp] = gid;
  }
  return Object.keys(mapping).length ? mapping : null;
}

function fontFallbackForName(name) {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('times') || lower.includes('serif')) return '"Times New Roman", serif';
  if (lower.includes('calibri')) return 'Calibri, Arial, sans-serif';
  if (lower.includes('microsoft sans')) return '"Microsoft Sans Serif", Arial, sans-serif';
  if (lower.includes('courier') || lower.includes('mono')) return 'monospace';
  return 'Arial, sans-serif';
}

function weightFrom(font) {
  return Number(font?.weight) || (/bold|black|heavy|demi|semibold/i.test(String(font?.basefont || font?.name || '')) ? 700 : 400);
}

function styleFrom(font) {
  return String(font?.style || '').trim() || (/italic|oblique/i.test(String(font?.basefont || font?.name || '')) ? 'italic' : 'normal');
}

function readU16(bytes, o) { return (bytes[o] << 8) | bytes[o + 1]; }
function readU32(bytes, o) { return (((bytes[o] << 24) >>> 0) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3]) >>> 0; }
function writeU16(bytes, o, v) { bytes[o] = (v >>> 8) & 255; bytes[o + 1] = v & 255; }
function writeU32(bytes, o, v) { bytes[o] = (v >>> 24) & 255; bytes[o + 1] = (v >>> 16) & 255; bytes[o + 2] = (v >>> 8) & 255; bytes[o + 3] = v & 255; }

function parseTables(bytes) {
  const count = readU16(bytes, 4);
  const tables = new Map();
  for (let i = 0; i < count; i += 1) {
    const base = 12 + i * 16;
    const tag = String.fromCharCode(bytes[base], bytes[base + 1], bytes[base + 2], bytes[base + 3]);
    tables.set(tag, { recordOffset: base, checksum: readU32(bytes, base + 4), offset: readU32(bytes, base + 8), length: readU32(bytes, base + 12) });
  }
  return tables;
}

function getCustomGlyphSequence(bytes) {
  const tables = parseTables(bytes);
  const cmap = tables.get('cmap');
  if (!cmap) return null;
  const base = cmap.offset;
  const numTables = readU16(bytes, base + 2);
  let format0 = null;
  let unicode = false;
  for (let i = 0; i < numTables; i += 1) {
    const rec = base + 4 + i * 8;
    const platform = readU16(bytes, rec);
    const encoding = readU16(bytes, rec + 2);
    const offset = readU32(bytes, rec + 4);
    const sub = base + offset;
    const format = readU16(bytes, sub);
    if ((platform === 3 && (encoding === 1 || encoding === 10)) || platform === 0) {
      if (format === 4 || format === 12) unicode = true;
    }
    if (!format0 && format === 0) format0 = sub;
  }
  if (unicode) return { unicode: true };
  if (!format0) return null;
  const length = readU16(bytes, format0 + 2);
  if (length < 262) return null;
  const glyphs = [];
  for (let code = 1; code < 256; code += 1) glyphs.push(bytes[format0 + 6 + code]);
  return { unicode: false, glyphs };
}

function uniqueCharsForFont(template, font) {
  const seen = new Set();
  const out = [];
  const matches = (charFont) => {
    if (!charFont) return false;
    const xrefA = Number(charFont?.xref);
    const xrefB = Number(font?.xref);
    if (Number.isFinite(xrefA) && Number.isFinite(xrefB) && xrefA === xrefB) return true;
    const a = String(charFont?.css_family || '').trim();
    const b = String(font?.css_family || '').trim();
    if (a && b && a === b) return true;
    return false;
  };

  for (const page of Array.isArray(template?.pages) ? template.pages : []) {
    for (const char of Array.isArray(page?.characters) ? page.characters : []) {
      if (!matches(char?.font)) continue;
      const text = String(char?.text ?? '');
      for (const cp of Array.from(text)) {
        if (!seen.has(cp)) { seen.add(cp); out.push(cp); }
      }
    }
    for (const field of Array.isArray(page?.fields) ? page.fields : []) {
      const fieldFont = { ...(field?.font || {}), ...(field?.font_lock || {}) };
      if (!matches(fieldFont)) continue;
      for (const cp of Array.from(String(field?.text ?? ''))) {
        if (!seen.has(cp)) { seen.add(cp); out.push(cp); }
      }
      for (const sourceChar of Array.isArray(field?.source_characters) ? field.source_characters : []) {
        for (const cp of Array.from(String(sourceChar?.text ?? ''))) {
          if (!seen.has(cp)) { seen.add(cp); out.push(cp); }
        }
      }
    }
  }
  return out;
}

function buildCmap4(mapping) {
  const entries = Object.entries(mapping)
    .map(([cp, gid]) => [Number(cp), Number(gid)])
    .filter(([cp, gid]) => Number.isFinite(cp) && cp >= 0 && cp <= 0xffff && gid > 0 && gid <= 0xffff)
    .sort((a, b) => a[0] - b[0]);
  if (!entries.length) return null;

  const segments = [...entries.map(([cp, gid]) => ({ start: cp, end: cp, delta: (gid - cp) & 0xffff })), { start: 0xffff, end: 0xffff, delta: 1 }];
  const segCount = segments.length;
  const power = Math.floor(Math.log2(segCount));
  const searchRange = 2 * (2 ** power);
  const entrySelector = power;
  const rangeShift = 2 * segCount - searchRange;
  const length = 16 + segCount * 8;
  const out = new Uint8Array(length);
  writeU16(out, 0, 4);
  writeU16(out, 2, length);
  writeU16(out, 4, 0);
  writeU16(out, 6, segCount * 2);
  writeU16(out, 8, searchRange);
  writeU16(out, 10, entrySelector);
  writeU16(out, 12, rangeShift);
  let off = 14;
  for (const seg of segments) { writeU16(out, off, seg.end); off += 2; }
  writeU16(out, off, 0); off += 2;
  for (const seg of segments) { writeU16(out, off, seg.start); off += 2; }
  for (const seg of segments) { writeU16(out, off, seg.delta); off += 2; }
  for (let i = 0; i < segCount; i += 1) { writeU16(out, off, 0); off += 2; }
  return out;
}

function concatBytes(...parts) {
  const length = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(length); let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

function writeS16(bytes, offset, value) {
  writeU16(bytes, offset, Number(value) & 0xffff);
}

function writeAscii4(bytes, offset, value) {
  const text = String(value || '').padEnd(4, ' ').slice(0, 4);
  for (let i = 0; i < 4; i += 1) bytes[offset + i] = text.charCodeAt(i) & 255;
}

function buildMinimalOs2(raw, tables, fontMeta, mapping) {
  const out = new Uint8Array(96);
  const head = tables.get('hhea');
  const ascender = head ? ((raw[head.offset + 4] << 8) | raw[head.offset + 5]) : 0;
  const descentRaw = head ? ((raw[head.offset + 6] << 8) | raw[head.offset + 7]) : 0;
  const descent = descentRaw & 0x8000 ? descentRaw - 0x10000 : descentRaw;
  const weight = Math.max(1, Math.min(1000, Number(fontMeta?.weight) || 400));
  const first = Math.min(...Object.keys(mapping).map(Number).filter((value) => Number.isFinite(value)), 32);
  const last = Math.max(...Object.keys(mapping).map(Number).filter((value) => Number.isFinite(value)), 0xffff);

  writeU16(out, 0, 4);
  writeS16(out, 2, 500);
  writeU16(out, 4, weight);
  writeU16(out, 6, 5);
  writeU16(out, 8, 0);
  writeS16(out, 10, 650); writeS16(out, 12, 699); writeS16(out, 14, 0); writeS16(out, 16, 140);
  writeS16(out, 18, 650); writeS16(out, 20, 699); writeS16(out, 22, 0); writeS16(out, 24, 479);
  writeS16(out, 26, 49); writeS16(out, 28, 258); writeS16(out, 30, 0);
  // PANOSE 32..41 remains zero.
  writeU32(out, 42, 0); writeU32(out, 46, 0); writeU32(out, 50, 0); writeU32(out, 54, 0);
  writeAscii4(out, 58, 'NONE');
  writeU16(out, 62, String(fontMeta?.style || '').toLowerCase() === 'italic' ? 0x01 : 0x40);
  writeU16(out, 64, first & 0xffff); writeU16(out, 66, last & 0xffff);
  writeS16(out, 68, ascender || 0); writeS16(out, 70, descent || 0); writeS16(out, 72, 0);
  writeU16(out, 74, Math.max(0, ascender || 0)); writeU16(out, 76, Math.max(0, Math.abs(descent || 0)));
  writeU32(out, 78, 0); writeU32(out, 82, 0);
  writeS16(out, 86, 0); writeS16(out, 88, 0);
  writeU16(out, 90, 0); writeU16(out, 92, 32); writeU16(out, 94, 2);
  return out;
}

function buildUnicodeOnlyCmap(mapping) {
  const cmap4 = buildCmap4(mapping);
  if (!cmap4) return null;
  const table = new Uint8Array(4 + 8 + cmap4.length);
  writeU16(table, 0, 0);
  writeU16(table, 2, 1);
  writeU16(table, 4, 3);
  writeU16(table, 6, 1);
  writeU32(table, 8, 12);
  table.set(cmap4, 12);
  return table;
}

function rebuildTrueTypeFontWithUnicodeMap(raw, mapping, fontMeta) {
  const sourceTables = parseTables(raw);
  const cmapData = buildUnicodeOnlyCmap(mapping);
  if (!cmapData) return null;

  const entries = [];
  for (const [tag, table] of sourceTables.entries()) {
    if (tag === 'cmap') {
      entries.push({ tag, data: cmapData });
      continue;
    }
    const data = raw.slice(table.offset, table.offset + table.length);
    entries.push({ tag, data });
  }

  if (!sourceTables.has('OS/2')) {
    entries.push({ tag: 'OS/2', data: buildMinimalOs2(raw, sourceTables, fontMeta, mapping) });
  }

  const numTables = entries.length;
  const power = Math.floor(Math.log2(Math.max(1, numTables)));
  const maxPower = 2 ** power;
  const searchRange = 16 * maxPower;
  const entrySelector = power;
  const rangeShift = numTables * 16 - searchRange;
  const headerLength = 12 + numTables * 16;

  let cursor = headerLength;
  const placements = entries.map((entry) => {
    cursor = (cursor + 3) & ~3;
    const placement = { ...entry, offset: cursor };
    cursor += (entry.data.length + 3) & ~3;
    return placement;
  });

  const out = new Uint8Array(cursor);
  out.set(raw.subarray(0, 4), 0);
  writeU16(out, 4, numTables);
  writeU16(out, 6, searchRange);
  writeU16(out, 8, entrySelector);
  writeU16(out, 10, rangeShift);

  const recordByTag = new Map();
  placements.forEach((entry, index) => {
    const record = 12 + index * 16;
    recordByTag.set(entry.tag, { record, entry });
    for (let i = 0; i < 4; i += 1) out[record + i] = entry.tag.charCodeAt(i) & 255;
    out.set(entry.data, entry.offset);
    const paddedEnd = entry.offset + ((entry.data.length + 3) & ~3);
    const checksum = checksum32(out, entry.offset, paddedEnd);
    writeU32(out, record + 4, checksum);
    writeU32(out, record + 8, entry.offset >>> 0);
    writeU32(out, record + 12, entry.data.length >>> 0);
  });

  const headEntry = recordByTag.get('head');
  if (!headEntry) return null;
  writeU32(out, headEntry.entry.offset + 8, 0);
  writeU32(out, headEntry.record + 4, checksum32(out, headEntry.entry.offset, headEntry.entry.offset + ((headEntry.entry.data.length + 3) & ~3)));

  const adjustment = (0xB1B0AFBA - checksum32(out)) >>> 0;
  writeU32(out, headEntry.entry.offset + 8, adjustment);

  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < out.length; i += chunk) binary += String.fromCharCode(...out.subarray(i, Math.min(i + chunk, out.length)));
  return btoa(binary);
}

function checksum32(bytes, start = 0, end = bytes.length) {
  let sum = 0;
  for (let offset = start; offset < end; offset += 4) {
    sum = (sum + readU32(bytes, offset)) >>> 0;
  }
  return sum >>> 0;
}

function patchTrueTypeCmap(base64, mapping, fontMeta = {}) {
  const raw = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const custom = getCustomGlyphSequence(raw);
  if (!custom || custom.unicode) return { base64, patched: false };
  const rebuilt = rebuildTrueTypeFontWithUnicodeMap(raw, mapping, fontMeta);
  if (!rebuilt) return { base64, patched: false };
  return { base64: rebuilt, patched: true };
}

function prepareFont(template, font) {
  const key = `${Number(font?.xref) || 0}|${String(font?.css_family || font?.basefont || font?.name || '')}|${String(font?.embedded?.sha256 || '')}`;
  if (FONT_CACHE.has(key)) return FONT_CACHE.get(key);

  const localInfo = localFontInfoFor(font);
  const originalAlias = String(font?.css_family || localInfo?.cssFamily || '').trim();
  const originalName = String(font?.basefont || font?.name || localInfo?.label || '').trim() || 'Arial';
  const fallbackStack = `${fontFallbackForName(originalName)}`;
  const result = {
    alias: originalAlias,
    originalName,
    family: originalAlias ? `"${esc(originalAlias)}", "${esc(originalName)}", ${fallbackStack}` : `"${esc(originalName)}", ${fallbackStack}`,
    weight: weightFrom(font),
    style: styleFrom(font),
    sizePt: Number(font?.size_pt) || Number(font?.size) || 12,
    face: font,
    embeddedBase64: font?.embedded?.base64 || null,
    localUrl: localInfo?.url || localFontUrlFor(font),
    patched: false
  };

  const data = font?.embedded?.base64;
  const isSubset = Boolean(font?.is_subset) || /^[A-Z]{6}\+/.test(String(font?.basefont || ''));
  if (data && originalAlias && isSubset) {
    try {
      const custom = getCustomGlyphSequence(Uint8Array.from(atob(data), (c) => c.charCodeAt(0)));
      if (custom && !custom.unicode) {
        const chars = uniqueCharsForFont(template, font);
        const glyphs = custom.glyphs || [];
        const mapping = {};
        const mappingChars = chars.length
          ? chars
          : (isMatriceBitsFont(font) ? MATRICE_BITS_UNICODE_SEQUENCE : []);
        for (let i = 0; i < Math.min(mappingChars.length, glyphs.length); i += 1) {
          const cp = mappingChars[i].codePointAt(0);
          const gid = glyphs[i];
          if (cp <= 0xffff && gid > 0) mapping[cp] = gid;
        }
        const patched = patchTrueTypeCmap(data, mapping, font);
        if (patched.patched) {
          result.alias = `${originalAlias}__UNICODE`;
          result.family = `"${esc(result.alias)}"`;
          result.embeddedBase64 = patched.base64;
          result.localUrl = '';
          result.patched = true;
        }
      }
    } catch {}
  }
  FONT_CACHE.set(key, result);
  return result;
}

function resolveTemplatePdfFont(template, font = {}) {
  const fonts = Array.isArray(template?.fonts) ? template.fonts : [];
  const cssFamily = String(font?.css_family || '').trim();
  if (cssFamily) {
    const hit = fonts.find((item) => String(item?.css_family || '').trim() === cssFamily);
    if (hit) return hit;
  }
  const xref = Number(font?.xref);
  if (Number.isFinite(xref)) {
    const hit = fonts.find((item) => Number(item?.xref) === xref);
    if (hit) return hit;
  }
  const wanted = [font?.basefont, font?.name].map(normalize).filter(Boolean);
  if (!wanted.length) return null;
  return fonts.find((item) => {
    const candidates = [item?.basefont, item?.name, item?.embedded?.basename].map(normalize).filter(Boolean);
    return wanted.some((w) => candidates.some((c) => c === w || c.includes(w) || w.includes(c)));
  }) || null;
}

function findTemplateFontForChoice(template, choice) {
  const fonts = Array.isArray(template?.fonts) ? template.fonts : [];
  if (!choice || !fonts.length) return null;
  const wanted = [
    ...(choice.templateNames || []),
    choice.label,
    choice.id,
  ].map(normalize).filter(Boolean);
  if (!wanted.length) return null;
  return fonts.find((font) => {
    const candidates = [
      font?.css_family,
      font?.basefont,
      font?.name,
      font?.embedded?.basename,
    ].map(normalize).filter(Boolean);
    return wanted.some((name) => candidates.some((candidate) =>
      candidate === name || candidate.includes(name) || name.includes(candidate)
    ));
  }) || null;
}

function localChoiceFamily(choice) {
  const system = String(choice?.systemFamily || '').trim();
  const local = String(choice?.cssFamily || '').trim();
  const fallback = choice?.label ? fontFallbackForName(choice.label) : 'Arial, sans-serif';
  if (local && isMatriceBitsFont(choice)) {
    return `"${esc(local)}", ${fallback}`;
  }
  if (system && local) return `"${esc(system)}", "${esc(local)}", ${fallback}`;
  if (local) return `"${esc(local)}", ${fallback}`;
  return fallback;
}

async function ensureUniversalMatriceBitsFont() {
  if (typeof document === 'undefined' || typeof FontFace !== 'function') return false;
  if (MATRICE_BITS_UNIVERSAL_CACHE.has('loaded')) return MATRICE_BITS_UNIVERSAL_CACHE.get('loaded');
  if (MATRICE_BITS_UNIVERSAL_CACHE.has('pending')) return MATRICE_BITS_UNIVERSAL_CACHE.get('pending');

  const pending = (async () => {
    try {
      const response = await fetch(MATRICE_BITS_URL, { cache: 'force-cache' });
      if (!response.ok) return false;
      const bytes = new Uint8Array(await response.arrayBuffer());
      const mapping = buildMatriceBitsUniversalMapping(bytes);
      if (!mapping) return false;
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)));
      }
      const base64 = btoa(binary);
      const patched = patchTrueTypeCmap(base64, mapping, { name: 'MatriceBits-Z2', weight: 400, style: 'normal' });
      if (!patched.patched) return false;
      const face = new FontFace(
        MATRICE_BITS_FONT_ALIAS,
        `url("data:font/ttf;base64,${patched.base64}")`,
        { weight: '400', style: 'normal', display: 'block' }
      );
      await face.load();
      document.fonts.add(face);
      MATRICE_BITS_UNIVERSAL_CACHE.set('loaded', true);
      return true;
    } catch {
      return false;
    }
  })();

  MATRICE_BITS_UNIVERSAL_CACHE.set('pending', pending);
  const result = await pending;
  MATRICE_BITS_UNIVERSAL_CACHE.delete('pending');
  return result;
}

export async function exactMatriceBitsFontReady() {
  return ensureUniversalMatriceBitsFont();
}

export function exactPdfFontPresentation(template, field, overrideFont = null) {
  if (overrideFont) {
    const sourceFont = field?.font || {};
    const lock = field?.font_lock || {};
    const sizePt =
      Number(lock?.size_pt) ||
      Number(sourceFont?.size_pt) ||
      Number(sourceFont?.size) ||
      12;

    const templateFont = findTemplateFontForChoice(template, overrideFont);
    if (templateFont) {
      const prepared = prepareFont(template, templateFont);
      const resolvedAlias = String(prepared.alias || templateFont.css_family || templateFont.name || '').trim();
      if (resolvedAlias) {
        return {
          family: `"${esc(resolvedAlias)}"`,
          weight: Number(templateFont?.weight) || Number(overrideFont?.weight) || 400,
          style: String(templateFont?.style || overrideFont?.style || 'normal'),
          alias: resolvedAlias,
          face: templateFont,
          fontRef: templateFont,
          sizePt,
          embeddedBase64: prepared.embeddedBase64 || null,
          localUrl: prepared.localUrl || '',
          patched: Boolean(prepared.patched),
          selectedFont: overrideFont,
          templateMatched: true,
        };
      }
    }

    const overrideAlias = isMatriceBitsFont(overrideFont)
      ? MATRICE_BITS_FONT_ALIAS
      : (overrideFont.cssFamily || overrideFont.systemFamily || '');
    return {
      family: isMatriceBitsFont(overrideFont)
        ? `"${esc(MATRICE_BITS_FONT_ALIAS)}", monospace`
        : localChoiceFamily(overrideFont),
      weight: Number(overrideFont?.weight) || 400,
      style: String(overrideFont?.style || 'normal'),
      alias: overrideAlias,
      face: overrideFont,
      fontRef: overrideFont,
      sizePt,
      embeddedBase64: null,
      localUrl: isMatriceBitsFont(overrideFont) ? MATRICE_BITS_URL : (overrideFont.url || ''),
      patched: false,
      selectedFont: overrideFont,
      templateMatched: false,
    };
  }

  const lock = field?.font_lock || {};
  const sourceFont = field?.font || {};
  const fontRef = { ...sourceFont, ...lock };
  const face = resolveTemplatePdfFont(template, fontRef) || {};

  // Merge the field font with the resolved template font. Some legacy
  // templates keep useful font metadata directly on the character/span
  // while the template-level entry is incomplete. The field metadata must
  // therefore remain a valid source instead of being discarded.
  const effectiveFont = { ...face, ...sourceFont, ...lock };
  const sourceEntry = prepareFont(template, effectiveFont);
  const alias = String(
    lock?.css_family ||
    sourceFont?.css_family ||
    face?.css_family ||
    effectiveFont?.css_family ||
    sourceEntry.alias ||
    ''
  ).trim();
  const original = String(
    lock?.basefont ||
    sourceFont?.basefont ||
    sourceFont?.name ||
    face?.basefont ||
    face?.name ||
    effectiveFont?.basefont ||
    effectiveFont?.name ||
    'Arial'
  ).trim();
  const weight =
    Number(lock?.weight) ||
    Number(sourceFont?.weight) ||
    Number(face?.weight) ||
    weightFrom(effectiveFont) ||
    400;
  const style =
    String(lock?.style || sourceFont?.style || face?.style || effectiveFont?.style || '').trim() ||
    styleFrom(effectiveFont);
  const sizePt =
    Number(lock?.size_pt) ||
    Number(sourceFont?.size_pt) ||
    Number(sourceFont?.size) ||
    Number(face?.size_pt) ||
    Number(face?.size) ||
    Number(effectiveFont?.size_pt) ||
    Number(effectiveFont?.size) ||
    12;

  return {
    family: alias
      ? ((sourceEntry.localUrl || sourceEntry.patched)
        ? `"${esc(alias)}"`
        : `"${esc(alias)}", "${esc(original)}", ${fontFallbackForName(original)}`)
      : `"${esc(original)}", ${fontFallbackForName(original)}`,
    weight,
    style,
    alias,
    face: effectiveFont,
    fontRef,
    sizePt,
    embeddedBase64: sourceEntry.embeddedBase64,
    localUrl: sourceEntry.localUrl || '',
    patched: sourceEntry.patched
  };
}

export async function ensureExactPdfFont(template, field, overrideFont = null) {
  if (typeof document === 'undefined' || typeof FontFace !== 'function') return false;
  const presentation = exactPdfFontPresentation(template, field, overrideFont);
  if (isMatriceBitsFont(overrideFont || field?.font || {}) && !presentation.embeddedBase64) {
    const ready = await ensureUniversalMatriceBitsFont();
    if (ready) return true;
  }
  if (!presentation.alias || (!presentation.localUrl && !presentation.embeddedBase64)) return false;
  try {
    const localSource = presentation.localUrl ? `url(\"${presentation.localUrl}\")` : '';
    const dataExt = presentation.face?.embedded?.ext || 'ttf';
    const dataSource = presentation.embeddedBase64
      ? `url(\"data:${mimeFor(dataExt)};base64,${presentation.embeddedBase64}\")`
      : '';
    const source = [localSource, dataSource].filter(Boolean).join(', ');
    if (!source) return false;
    const face = new FontFace(presentation.alias, source, { weight: String(presentation.weight), style: presentation.style, display: 'block' });
    await face.load();
    document.fonts.add(face);
    return true;
  } catch {
    return false;
  }
}

export function useEmbeddedPdfFonts(template) {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const fonts = Array.isArray(template?.fonts) ? template.fonts : [];
    const style = document.createElement('style');
    const localCss = LOCAL_FONT_CATALOG.filter((font) => !isMatriceBitsFont(font)).map((font) => {
      const source = localFontSource(font);
      return source
        ? `@font-face{font-family:"${esc(font.cssFamily)}";src:${source};font-weight:${font.weight};font-style:${font.style};font-display:block;font-synthesis:none;}`
        : '';
    }).filter(Boolean).join('\n');
    style.dataset.pdfTemplateFonts = 'field-font-unicode-v31';
    style.textContent = `${localCss}\n${fonts.map((font) => {
      if (isMatriceBitsFont(font) && !font?.embedded?.base64) return '';
      if (!font?.embedded?.base64 && !localFontUrlFor(font)) return '';
      const prepared = prepareFont(template, font);
      if (!prepared.alias || (!prepared.embeddedBase64 && !prepared.localUrl)) return '';
      const format = formatFor(font?.embedded?.ext);
      const fmt = format ? ` format("${format}")` : '';
      const sources = [];
      if (prepared.localUrl) sources.push(`url("${prepared.localUrl}")${fmt}`);
      if (prepared.embeddedBase64) sources.push(`url("data:${mimeFor(font?.embedded?.ext)};base64,${prepared.embeddedBase64}")${fmt}`);
      if (!sources.length) return '';
      return `@font-face{font-family:"${esc(prepared.alias)}";src:${sources.join(',')};font-weight:${prepared.weight};font-style:${prepared.style};font-display:block;font-synthesis:none;}`;
    }).filter(Boolean).join('\n')} `;
    if (style.textContent) document.head.appendChild(style);
    let cancelled = false;
    const load = async () => {
      try { await Promise.all(LOCAL_FONT_CATALOG.map((font) => ensureLocalFont(font))); } catch {}
      try {
        const hasMatriceLegacy = (Array.isArray(template?.pages) ? template.pages : []).some((page) =>
          (Array.isArray(page?.characters) ? page.characters : []).some((char) => isMatriceBitsFont(char?.font || {})) ||
          (Array.isArray(page?.fields) ? page.fields : []).some((field) => isMatriceBitsFont({ ...(field?.font || {}), ...(field?.font_lock || {}) }))
        );
        if (hasMatriceLegacy) await ensureUniversalMatriceBitsFont();
      } catch {}
      try { await Promise.all(fonts.map(async (font) => {
        const prepared = prepareFont(template, font);
        if (!prepared.alias || (!prepared.embeddedBase64 && !prepared.localUrl) || typeof FontFace !== 'function') return;
        try {
          const localSource = prepared.localUrl ? `url("${prepared.localUrl}")` : '';
          const dataSource = prepared.embeddedBase64 ? `url("data:${mimeFor(font?.embedded?.ext)};base64,${prepared.embeddedBase64}")` : '';
          const source = [localSource, dataSource].filter(Boolean).join(', ');
          if (!source) return;
          const face = new FontFace(prepared.alias, source, { weight: String(prepared.weight), style: prepared.style, display: 'block' });
          await face.load();
          if (!cancelled) document.fonts.add(face);
        } catch {}
      })); } catch {}
      try { await document.fonts.ready; } catch {}
      if (!cancelled) setRevision((value) => value + 1);
    };
    load();
    return () => { cancelled = true; style.remove(); };
  }, [template]);
  return revision;
}

export { mimeFor, formatFor, prepareFont, patchTrueTypeCmap, getCustomGlyphSequence, localFontUrlFor, localFontInfoFor };
